import { RampConfig, RampShape } from '@gorampe/calc-engine';
import type { HardwareGroup } from './hardware/instances';

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}
export interface SceneLayout {
  platforms: PlatformPlacement[];
  treads: TreadPlacement[];
  rails: RailPlacement[];
  landings: LandingPlacement[];
  pattes: PattePlacement[];
  cornerPosts: CornerPostPlacement[];
  transitions: TransitionPlacement[];
  hardwareGroups: HardwareGroup[];
  /**
   * Floor type ("plein" or "mailles") propagated from RampConfig.floor.
   * Used by aggregateBOM to pick the correct catalog SKU variant
   * (e.g. F01005 vs F01001 for platforms). Optional for backwards
   * compatibility with synthetic layouts that don't carry this info.
   */
  floor?: 'plein' | 'mailles';
}

export interface CornerPostPlacement {
  id: string;
  position: Vec3; // base (foot) of post, scene units
  heightIn: number;
  partNumber?: string;
}

export interface TransitionPlacement {
  id: string;
  position: Vec3; // center of plate at top edge, scene units
  rotationY: number;
  widthIn: number;
  partNumber?: string;
}

export interface PattePlacement {
  id: string;
  position: Vec3; // top-center of patte, scene units
  rotationY: number;
  widthIn: number; // width spanning under tread (e.g. 36)
  heightIn: number; // distance from tread bottom to ground (computed from position.y)
  partNumber?: string; // F04xxx
}

export interface PlatformPlacement {
  id: string;
  position: Vec3;
  widthIn: number;
  depthIn: number;
  kind: 'head' | 'turn-landing' | 'rest-landing';
}

export interface TreadPlacement {
  id: string;
  position: Vec3;
  rotationY: number; // for L90/U180 turns
  pitchAngle: number; // inclination in radians
  lengthIn: number;
  widthIn: number;
}

export interface RailPlacement {
  id: string;
  position: Vec3;
  rotationY: number;
  pitchAngle?: number; // X-axis tilt to match ramp slope (radians)
  lengthIn: number;
  heightIn: number; // 36 or 42 inches
  type: 'ajoure' | 'barrotin' | 'barrotin_mains';
  side: 'left' | 'right';
}

export interface LandingPlacement {
  id: string;
  position: Vec3;
  widthIn: number;
  depthIn: number;
}

export interface RampSceneInput {
  config: RampConfig;
}

export const SCALE_IN_TO_UNIT = 1 / 12;

// Re-export for convenience
export type { RampConfig, RampShape };
